
import { Component, Prop, Ref, Vue } from "vue-property-decorator";
import { State, Action, namespace, Getter, Mutation } from "vuex-class";
import VuePerfectScrollbar from "vue-perfect-scrollbar";
import { debounce, get } from "lodash";
import axios, { CancelTokenSource, CancelToken } from "axios";

import api from "@/services/api.service";
import { GroupWithPermissions } from "@/store/areas/types";
import { FeaturesList, UserPermissions } from "@/store/types";
import { Response } from "@/store/responses/types";
import {
	VueBootstrapField,
	UserGroup
} from "@/store/types";
import ActionSetupModal from "@/components/setup/alarm-setup/actions/ActionSetupModal.vue";
import AlarmSetupModal from "@/components/setup/alarm-setup/AlarmSetupModal.vue";
import AlarmTriggerTest from "@/components/setup/alarm-setup/AlarmTriggerTest.vue";
import SureViewIcon from "@/components/SureViewIcon.vue";

// Bulk Import
import BulkImportModal from "@/components/bulk-import/BulkImportModal.vue";
import { MetaData, BulkTypeData, MapValues, AlarmBulkType, RawAlarmBulkType } from '@/bulk-import/AlarmBulkImport';
import BulkImport, { BulkType, ImportBulkType } from '@/bulk-import/BulkImport';

const Areas = namespace("areas");
const Responses = namespace("responses");
const UserManagement = namespace("userManagement");
const Setup = namespace("setup");
const debouncePeriod: number = process.env.NODE_ENV === "test" ? 1 : 500;

@Component({
	components: {
		"action-setup-modal": ActionSetupModal,
		"alarm-setup-modal": AlarmSetupModal,
		"alarm-trigger-test": AlarmTriggerTest,
		"sureview-icon": SureViewIcon,
		"vue-perfect-scrollbar": VuePerfectScrollbar,
		"bulk-import-modal": BulkImportModal,
	}
})
export default class AlarmSetupTable extends Vue {
	@Prop({ required: false, default: 0 }) private selectedServerId: number;
	@Prop({ default: false, type: Boolean }) private queryable!: boolean;
	@Prop({ default: true, type: Boolean }) private bulkImport!: boolean;

	@Getter("getFeaturesList") private featuresList: FeaturesList;
	@Getter("getPermissions") private permissions: UserPermissions;
	@Getter private getFeature: (featureNames: string[]) => boolean;
	@Action private fetchUserGroups: () => Promise<void>;
	@State private UserGroups: UserGroup[];

	@Mutation private setIsLoading: (loading: boolean) => void;

	@Responses.State private alarmActionsOpened: boolean;
	@Responses.State private pageIndex: number;
	@Responses.State private pageSize: number;
	@Responses.State private filter: string;
	@Responses.State private filterPageIndex: number;
	@Responses.State private responseTableIsBusy: boolean;
	@Responses.State private currentAlarm: Response;
	@Responses.Action private fetchResponses: ({ cancelToken, serverId }: { cancelToken: string, serverId: number }) => Promise<void>;
	@Responses.Action private getResponseById: (Response) => Promise<void>;
	@Responses.Action private getSupportedAlarmActionTypes: () => Promise<void>;
	@Responses.Getter("getResponsesOrFilteredResponses") private responsesOrFilteredResponses: Response[];
	@Responses.Getter("getAllResponsesRetrieved") private allResponsesRetrieved: boolean;
	@Responses.Mutation private resetState: () => void;
	@Responses.Mutation private updateResponse: (response: Response) => void;
	@Responses.Mutation private setAlarmModalVisible: (isVisible: boolean) => void;
	@Responses.Mutation private setAlarmActionsVisible: (isVisible: boolean) => void;
	@Responses.Mutation private setCurrentAlarm: (alarm: Response) => void;
	@Responses.Mutation private setPageIndex: (pageIndex: number) => void;
	@Responses.Mutation private resetFilteredResponses: () => void;
	@Responses.Mutation private setFilter: (filter: string) => void;
	@Responses.Mutation private setFilterPageIndex: (pageIndex: number) => void;
	@Responses.Mutation private setResponseTableIsBusy: (isBusy: boolean) => void;
	@Responses.Mutation setAlarmSetupModalLoaded: (flag: boolean) => void;

	@UserManagement.Action private loadContactsList: () => Promise<void>;

	@Setup.Action loadResponseAlarmTypes: () => Promise<void>;

	private searchTerm: string = "";
	private defaultResponseGrouping: string;
	private loadingMoreEntries: boolean = false;
	private triggerTestAlarmOpened: boolean = false;
	private bulkImportModal: boolean = false;
	public responseSearchToken: CancelTokenSource = null;

	@Ref() private search: HTMLElement;

	private tableFields: VueBootstrapField[] = [
		{ key: "title", label: "Alarm Title", sortable: true },
		{ key: "serverTypeEventTitle", label: "Type", sortable: true },
		{ key: "input1", label: "Input1", sortable: true },
		{ key: "input2", label: "Input2", sortable: true },
		{
			key: "groupID",
			label: "Area",
			sortable: true,
			formatter: groupID => {
				return this.getGroupTitle(groupID);
			}
		},
		{ key: "deviceTitle", label: "Device", sortable: true },
		{ key: "priority", label: "Priority", sortable: true },
		{ key: "armed", label: "Armed", sortable: true },
		{ key: "autoHandle", label: "Auto Handle", sortable: true },
		{ key: "matchGeofence", label: "Match Geofence", sortable: true }
	];

	private getGroupTitle(groupID: number): string {
		return this.UserGroups.find(ug => ug.groupID == groupID)?.title;
	}

	private getAlarmGroupingTitle(alarm: Response): string {
		if (alarm.isRaiseIndividual) return "No Grouping";
		if (alarm.isRaiseGrouped) return "By Response";
		return this.defaultResponseGrouping;
	}

	private async created(): Promise<void> {
		this.resetState();

		if (this.hasAdvancedAlarmGroupingFlag) {
			this.tableFields.push({
				key: "alarmGrouping",
				label: "Alarm Grouping",
				sortable: true,
				formatter: (value, key, item) => this.getAlarmGroupingTitle(item)
			});
		}

		if (this.permissions.isAdmin || this.permissions.canEditSiteSetup) {
			this.tableFields.push({ key: "actions", label: "Actions", sortable: false });
		}

		this.setResponseTableIsBusy(true);
		await this.getResponses(this.responseSearchToken?.token);
		this.setResponseTableIsBusy(false);
		if (this.hasAlarmActionsFlag) {
			await Promise.all([this.getSupportedAlarmActionTypes(), this.loadContactsList()]);
		}
	}

	private handleNewAlarmClick(): void {
		this.setAlarmSetupModalLoaded(true);
		this.setIsLoading(true);
		this.setCurrentAlarm({} as Response);
		this.setAlarmModalVisible(true);
	}

	private async handleEditAlarmClick(alarm: Response): Promise<void> {

		this.setAlarmSetupModalLoaded(true);
		if (this.currentAlarm?.groupID != alarm?.groupID) {
			this.setIsLoading(true);
		}
		await this.getResponseById(alarm.responseID);
		this.updateResponse(this.currentAlarm);
		this.setAlarmModalVisible(true);

	}

	private handleActionsClick(alarm: Response): void {
		this.setCurrentAlarm(alarm);
		this.setAlarmActionsVisible(true);
	}

	private async handleScrollBarReachEnd(): Promise<void> {
		if (this.allResponsesRetrieved || this.responseTableIsBusy || this.loadingMoreEntries) return;

		this.filter ? this.setFilterPageIndex(this.filterPageIndex + 1) : this.setPageIndex(this.pageIndex + 1);
		this.loadingMoreEntries = true;
		await this.getResponses(this.responseSearchToken?.token);
		this.loadingMoreEntries = false;
	}

	private async handleSearchInput(): Promise<void> {
		this.setResponseTableIsBusy(true);
		await this.performSearch();
	}

	private performSearch = debounce(
		async function() {
			if (this.responseSearchToken) {
				this.responseSearchToken.cancel("Cancel Search");
			}

			this.responseSearchToken = axios.CancelToken.source();

			this.resetFilteredResponses();
			this.setFilter(this.searchTerm);

			try {
				await this.getResponses(this.responseSearchToken?.token);
			} catch (e) {
				return; //Return when past requests are cancelled
			}

			this.setResponseTableIsBusy(false);
		},
		debouncePeriod,
		{ leading: false, trailing: true }
	);

	private async getResponses(cancelToken): Promise<void> {
		await Promise.all([
			this.fetchResponses({ cancelToken, serverId: this.selectedServerId }),
			this.getDefaultResponseGrouping(cancelToken),
			this.loadResponseAlarmTypes(),
			this.fetchUserGroups()
		]);
	}

	/** If the alarm doesn't have any grouping options set,
	 * then grouping is set in accordance with settings  */
	private async getDefaultResponseGrouping(cancelToken: CancelToken): Promise<void> {
		const { data } = await api.getDefaultResponseGrouping(cancelToken);
		this.defaultResponseGrouping = data;
	}

	private get hasAdvancedAlarmGroupingFlag(): boolean {
		return get(this.featuresList, ["Alarms", "AdvancedAlarmGrouping"]);
	}

	private get hasAlarmActionsFlag(): boolean {
		return get(this.featuresList, ["AlarmSetup", "Actions"]);
	}

	private get canTriggerTestAlarm(): boolean {
		return (
			get(this.featuresList, ["AlarmSetup", "TestAlarm"]) &&
			(this.permissions.isAdmin || this.permissions.canTriggerTestAlarm)
		);
	}

	private handleTestAlarmClick(alarm: Response): void {
		this.setCurrentAlarm(alarm);
		this.triggerTestAlarmOpened = true;
	}

	private handleAlarmTriggerTestClosed(): void {
		this.triggerTestAlarmOpened = false;
	}

	/**
	 * @summary computed property which will return all the bulk import
	 * @summary types and meta-data for alarm seutp.
	 */
	private get bulkImports(): ImportBulkType<AlarmBulkType, RawAlarmBulkType>[] {
		return [{
			bulkType: BulkTypeData,
			metaData: MetaData,
			mapValues: MapValues,
		}]
	}
}
